import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BedDouble, DoorOpen, NotebookPen } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Checkbox, Field, FormSkeleton, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { UNIDENTIFIED_DEATH_NOTICE, type ErCaseDetail } from './er-board';

export function ErDispositionPage() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/er');

  const detail = useQuery({
    queryKey: ['er-case', caseId],
    queryFn: () => get<ErCaseDetail>(`/er/cases/${caseId}`),
    enabled: Boolean(caseId),
  });

  if (!detail.data) {
    return (
      <FormScreen title="Disposition" icon={DoorOpen} backTo={returnTo}>
        {detail.isLoading ? (
          <FormSkeleton />
        ) : (
          <Alert variant="destructive">{describeError(detail.error)}</Alert>
        )}
      </FormScreen>
    );
  }

  return <ErDispositionForm key={detail.data.id} erCase={detail.data} returnTo={returnTo} />;
}

function ErDispositionForm({ erCase, returnTo }: { erCase: ErCaseDetail; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [unidentifiedDeathRecorded, setUnidentifiedDeathRecorded] = useState(false);
  const [disposition, setDisposition] = useState('discharged');
  const [notes, setNotes] = useState('');
  const [referredTo, setReferredTo] = useState('');
  const [causeOfDeath, setCauseOfDeath] = useState('');
  const [issueCertificate, setIssueCertificate] = useState(true);
  const [bedId, setBedId] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const admitting = disposition === 'admitted';
  const expired = disposition === 'expired';
  const who = `${erCase.patient?.fullName ?? erCase.unknownPatientLabel ?? 'Unidentified'} · ${erCase.number}`;

  const beds = useQuery({
    queryKey: ['vacant-beds', 'er'],
    queryFn: () => get<{ id: string; code: string; bedClass: string; room: { ward: { name: string } } }[]>('/ipd/beds/vacant'),
    enabled: admitting,
  });

  const doctors = useQuery({
    queryKey: ['doctors'],
    queryFn: async () => (await get<{ data: { id: string; name: string }[] }>('/org/clinicians?kind=doctors&limit=200')).data,
    enabled: admitting,
  });

  const dispose = useMutation({
    mutationFn: () =>
      post(`/er/cases/${erCase.id}/disposition`, {
        disposition,
        notes: notes || undefined,
        referredTo: referredTo || undefined,
        causeOfDeath: expired ? causeOfDeath : undefined,
        admission: admitting ? { bedId, admittingDoctorId: doctorId } : undefined,
      }),
    onSuccess: async () => {
      toast.success('Episode closed', admitting ? 'The patient is now admitted' : undefined);
      if (expired && issueCertificate && erCase.patient) {
        try {
          const certificate = await post<{ id: string }>('/mortality/certificate', { erCaseId: erCase.id });
          const doc = await get<{ html: string }>(`/certificates/${certificate.id}/print`);
          printDocument(doc.html);
        } catch (caught) {
          toast.error('The death was recorded, but the certificate could not be issued', describeError(caught));
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['er-board'] });
      void queryClient.invalidateQueries({ queryKey: ['er-case', erCase.id] });
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
      if (expired && !erCase.patient) {
        setUnidentifiedDeathRecorded(true);
        return;
      }
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = !(admitting && (!bedId || !doctorId)) && !(expired && causeOfDeath.trim().length < 3);
  const dirty =
    !dispose.isSuccess &&
    (disposition !== 'discharged' || notes !== '' || referredTo !== '' || causeOfDeath !== '' || bedId !== '' || doctorId !== '');

  if (unidentifiedDeathRecorded) {
    return (
      <FormScreen title="Death recorded" description={who} icon={DoorOpen} backTo={returnTo}>
        <Alert variant="warning">{UNIDENTIFIED_DEATH_NOTICE}</Alert>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(returnTo, { replace: true })}>
            Close
          </Button>
          <Button type="button" onClick={() => navigate(`/er?case=${erCase.id}`, { replace: true })}>
            Open the case
          </Button>
        </div>
      </FormScreen>
    );
  }

  return (
    <FormScreen
      title="Disposition"
      description={who}
      icon={DoorOpen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        dispose.mutate();
      }}
    >
      <FormSection title={tr('opd', 'outcome')} icon={DoorOpen}>
        <Field label="Outcome" required className="sm:col-span-2">
          <Select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
            {['discharged', 'admitted', 'referred', 'lama', 'absconded', 'expired'].map((d) => (
              <option key={d} value={d}>
                {humanize(d)}
              </option>
            ))}
          </Select>
        </Field>

        {expired && (
          <>
            <Field label="Cause of death" required hint="Written on the death certificate and counted in the mortality report" className="sm:col-span-2">
              <Input value={causeOfDeath} onChange={(e) => setCauseOfDeath(e.target.value)} />
            </Field>
            {erCase.patient ? (
              <div className="sm:col-span-2">
                <Checkbox checked={issueCertificate} onCheckedChange={setIssueCertificate} label="Issue and print the death certificate now" />
              </div>
            ) : (
              <Alert variant="warning" className="sm:col-span-2">
                {UNIDENTIFIED_DEATH_NOTICE}
              </Alert>
            )}
          </>
        )}

        {disposition === 'referred' && (
          <Field label="Referred to" required className="sm:col-span-2">
            <Input value={referredTo} onChange={(e) => setReferredTo(e.target.value)} />
          </Field>
        )}
      </FormSection>

      {admitting && (
        <FormSection title={tr('opd', 'admission')} icon={BedDouble}>
          <Field label="Bed" required>
            <Select value={bedId} onChange={(e) => setBedId(e.target.value)}>
              <option value="">Select a free bed…</option>
              {beds.data?.map((bed) => (
                <option key={bed.id} value={bed.id}>
                  {bed.room.ward.name} · {bed.code} ({humanize(bed.bedClass, 'bed_class')})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Admitting consultant" required>
            <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
              <option value="">Select…</option>
              {doctors.data?.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </Field>
        </FormSection>
      )}

      <FormSection title={tr('opd', 'notes')} icon={NotebookPen} columns={1}>
        <Field label="Notes">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Close episode" loading={dispose.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
